const SLOW_REQUEST_MS = 3000;
const SLOW_NOTICE_MS = 4000;

let browserOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
let apiReachable = null;
let slow = false;
let slowUntil = 0;
let slowResetTimer = null;
let snapshot = {
    browserOnline,
    apiReachable,
    available: browserOnline && apiReachable !== false,
    slow,
};

const subscribers = new Set();
const pendingRequests = new Map();
let listening = false;

const publish = () => {
    snapshot = {
        browserOnline,
        apiReachable,
        available: browserOnline && apiReachable !== false,
        slow,
    };
    subscribers.forEach((subscriber) => subscriber());
};

const refreshSlowState = () => {
    const hasSlowRequest = [...pendingRequests.values()].some(
        (request) => Date.now() - request.startedAt >= SLOW_REQUEST_MS
    );
    slow = hasSlowRequest || Date.now() < slowUntil;
    if (slowResetTimer) {
        clearTimeout(slowResetTimer);
        slowResetTimer = null;
    }
    if (slow && !hasSlowRequest) {
        slowResetTimer = setTimeout(() => {
            slowResetTimer = null;
            refreshSlowState();
            publish();
        }, Math.max(0, slowUntil - Date.now()));
    }
};

const handleOffline = () => {
    browserOnline = false;
    apiReachable = false;
    slow = false;
    slowUntil = 0;
    pendingRequests.forEach(({ timer }) => clearTimeout(timer));
    pendingRequests.clear();
    if (slowResetTimer) clearTimeout(slowResetTimer);
    slowResetTimer = null;
    publish();
};

const handleOnline = () => {
    browserOnline = true;
    publish();
};

const startListening = () => {
    if (listening || typeof window === 'undefined') return;
    listening = true;
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    if (navigator.onLine === false) handleOffline();
    else handleOnline();
};

const stopListening = () => {
    if (!listening || typeof window === 'undefined') return;
    listening = false;
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
};

export const getNetworkStatus = () => snapshot;

export const subscribeToNetworkStatus = (subscriber) => {
    subscribers.add(subscriber);
    startListening();
    return () => {
        subscribers.delete(subscriber);
        if (subscribers.size === 0) stopListening();
    };
};

export const markApiReachable = () => {
    browserOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
    apiReachable = browserOnline;
    publish();
};

export const markApiUnavailable = () => {
    browserOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
    apiReachable = false;
    publish();
};

export const markApiRequestStarted = (requestId) => {
    if (!browserOnline || requestId == null) return;
    const timer = setTimeout(() => {
        if (!pendingRequests.has(requestId)) return;
        slow = true;
        publish();
    }, SLOW_REQUEST_MS);
    pendingRequests.set(requestId, { startedAt: Date.now(), timer });
};

export const markApiRequestFinished = (requestId, reachable) => {
    const pending = pendingRequests.get(requestId);
    if (pending) {
        clearTimeout(pending.timer);
        pendingRequests.delete(requestId);
        if (Date.now() - pending.startedAt >= SLOW_REQUEST_MS) {
            slowUntil = Math.max(slowUntil, Date.now() + SLOW_NOTICE_MS);
        }
        refreshSlowState();
    }

    if (reachable) markApiReachable();
    else markApiUnavailable();
    publish();
};

export const resetNetworkStatusForTests = () => {
    browserOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
    apiReachable = null;
    slow = false;
    slowUntil = 0;
    pendingRequests.forEach(({ timer }) => clearTimeout(timer));
    pendingRequests.clear();
    if (slowResetTimer) clearTimeout(slowResetTimer);
    slowResetTimer = null;
    publish();
};